import mitraEmptyArt from '@/assets/mascot/mitra-empty.png'
import mitraNamasteArt from '@/assets/mascot/mitra-namaste.png'
import mitraOfflineArt from '@/assets/mascot/mitra-offline.png'
import mitraSearchArt from '@/assets/mascot/mitra-search.png'
import { buttonVariants } from '@/components/ui/button-variants'
import { cn } from '@/lib/utils'

/**
 * EmptyState's vocabulary, in a `.ts` sibling so the component file exports
 * only a component (Fast Refresh) — the split `button-variants.ts` makes.
 */

/** What happened. Picks the default copy, the default art and the live-region role. */
export type StateKind =
  'empty' | 'no-results' | 'not-available' | 'not-found' | 'failed' | 'offline'

/** The request did not work. The only kinds that draw "Try again". */
export type FailureKind = Extract<StateKind, 'failed' | 'offline'>

/** Every kind of "nothing to show" that is not a failure. */
export type EmptyKind = Exclude<StateKind, FailureKind>

/**
 * Where the block sits, which sets its scale:
 * - `page` — the whole screen or tab body (unframed, staged and vertically
 *   centred in the column below it — see `PAGE_STAGE` in EmptyState.tsx —
 *   except in `layout="banner"`, which stays unstaged).
 * - `section` — a card body, a table body, or its own full-width card: the
 *   same stack as `page`, inside its card.
 * - `inline` — one row inside a card, a listbox or a menu.
 * - `compact` — a chart box, a table cell, a rail tile.
 */
export type StateSize = 'page' | 'section' | 'inline' | 'compact'

export const isFailureKind = (kind: StateKind): kind is FailureKind =>
  kind === 'failed' || kind === 'offline'

/**
 * The owner's words for a feature with no endpoint behind it yet. Exported for
 * the value-level placeholders (a stat cell, a settings row) so they stop
 * drifting from the block's own title.
 */
export const NOT_AVAILABLE_TITLE = 'Not available yet'

interface StateCopy {
  title: string
  description: string
}

/**
 * Default copy per kind. The design's own words where a frame exists: failed
 * is "Couldn't load …", the form most screens already spell by hand, over the
 * Account and Newsletters frames' "Could not load …" — one voice for failure
 * app-wide beats two. Offline is the Market frame verbatim, no-results is
 * Spotlight's. `not-available` and `not-found` have no frame; their words are
 * the codebase's existing conventions.
 */
export function stateCopy(
  kind: StateKind,
  subject: { query?: string | undefined; thing?: string | undefined } = {},
): StateCopy {
  switch (kind) {
    case 'empty':
      return {
        title: 'Nothing here yet',
        description: 'It fills in once there is something to show.',
      }
    case 'no-results': {
      const query = subject.query?.trim() ?? ''
      return {
        title: query === '' ? 'No results' : `No match for “${query}”`,
        description: 'Try a different search, or clear the filters.',
      }
    }
    case 'not-available':
      return {
        title: NOT_AVAILABLE_TITLE,
        description: `${subject.thing ?? 'This'} isn't live yet. It will show here as soon as it is.`,
      }
    case 'not-found':
      return {
        title: 'Not found',
        description: 'The link may be wrong, or it was removed.',
      }
    case 'failed':
      return {
        title: "Couldn't load this",
        description: 'Check your connection and try again.',
      }
    case 'offline':
      return {
        title: 'You are offline',
        description:
          'There is no connection right now. Turn wifi or mobile data on, then try again.',
      }
  }
}

/**
 * The mascot each kind shows when its block stands alone.
 *
 * Offline is the design's own pose, `mitra-offline` (phone handset, shrugging,
 * `5327:4326`). `no-results` is too, now: `mitra-search` (magnifier,
 * `4243:102839`), exported for the 2026-09-28 design refresh. One pose is
 * still not in `src/assets/mascot/`, so a stand-in covers it — failed draws
 * `mitra-sad` (`4243:105204`), covered by `mitra-namaste`: folded hands read
 * as a polite apology and carry no market meaning. The older stand-in,
 * `mitra-flag-down`, waves a red down-arrow flag, which reads as "the market
 * fell" — the exact conflation tokens.semantic.css forbids for a failure.
 * The handset is not borrowed for failed either: it says "your connection",
 * and a failure can be the server's.
 *
 * TODO(tech-debt): swap in `mitra-sad` once it is exported (with
 * `mitra-watchlist-empty`, `mitra-compare` and `mitra-savings`, which the
 * family screens draw).
 */
export const STATE_ART: Record<StateKind, string> = {
  empty: mitraEmptyArt,
  'no-results': mitraSearchArt,
  'not-available': mitraEmptyArt,
  'not-found': mitraEmptyArt,
  failed: mitraNamasteArt,
  offline: mitraOfflineArt,
}

/** A retry in flight: the button stays focusable, so it is marked, not disabled. */
const BUSY = 'aria-disabled:cursor-progress aria-disabled:opacity-70'

/**
 * Text-link actions for the two small sizes, where a padded button would set
 * the height of the row or chart box it sits in. Below `lg` an invisible
 * `::before` grows the hit area to 44px tall, as IconButton does, without
 * moving anything — centred on the link, so the reach is 44 whatever the
 * line box. `label-m` already carries its 500 weight.
 */
const TEXT_ACTION = cn(
  'relative cursor-pointer text-label-m whitespace-nowrap touch-manipulation transition-colors',
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
  "before:absolute before:-inset-x-2 before:top-1/2 before:h-11 before:-translate-y-1/2 before:content-[''] lg:before:hidden",
  BUSY,
)

/**
 * The skin EmptyState puts on its own "Try again", for a caller's action to
 * wear too — a router `<Link>` included (see `button-variants.ts`), so a link
 * out of a state looks exactly like the retry beside it.
 *
 * Square Primary and Secondary buttons at page and section size, the inline
 * height the design draws on every state (`5327:4326`, Figma 34 → 36), which
 * Button grows to the 44px touch minimum below `lg`. Text links at inline and
 * compact size.
 */
export function stateActionClass(
  size: StateSize,
  variant: 'primary' | 'secondary' = 'primary',
): string {
  switch (size) {
    case 'page':
    case 'section':
      return cn(buttonVariants({ variant, size: 'md' }), BUSY)
    case 'inline':
    case 'compact':
      return cn(
        TEXT_ACTION,
        // The primary underlines on hover, as every text link does: a link
        // word has no quieter colour left to step to.
        variant === 'primary'
          ? 'text-link hover:underline focus-visible:underline'
          : 'text-muted-foreground hover:text-foreground',
      )
  }
}
